import * as React from "react"
import { useRecoilValue } from "recoil"
import { brandingSelector } from "../../../recoil/atoms/room"
import { CopyIcon } from "../../common/icons"
import {
  ModerationSubTitle,
  ModerationSettingsList,
  ModerationSettingsItem,
  ModerationSettingsName,
  ModerationLink
} from "./moderation.styled"
import { RoomLinksProps } from "./moderation.interfaces"
import { useTranslation } from "react-i18next"
import useMeetingClient from "../../../hooks/useMeetingClient"
import { Box, Flex, OrderedList, ListItem, Text } from "@chakra-ui/react"
import { IconButton } from "@chakra-ui/button"
import { useBrowserType } from "../../../hooks/useBrowserType"
import { IoClose } from "react-icons/io5"
import { Icon } from "../../common/icons"
import { TbQuestionMark } from "react-icons/tb"
import { blendWithWhiteBackground } from "../../../util"

const RTMPLink = React.memo(() => {
  const { t } = useTranslation()
  const roomStyles = useRecoilValue(brandingSelector)
  const meetingClient = useMeetingClient()
  const [showOverlay, setShowOVerlay] = React.useState(false)
  const roomLinks = React.useMemo<RoomLinksProps[]>(() => {
    return [
      {
        id: 10001,
        name: t("Link"),
        url: meetingClient.rtc.generateRTMPUrl?.()
      }
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t])

  return (
    <>
      <ModerationSubTitle
        style={{ color: roomStyles?.servicePanelElementSecondaryText }}
      >
        {t("RTMP LINK")}
      </ModerationSubTitle>
      <ModerationSettingsList>
        {roomLinks.map((link, index) => (
          <ModerationSettingsItem key={link.id}>
            <ModerationSettingsName
              roomStyles={roomStyles}
              style={{ marginRight: index === 0 ? "1.6rem" : "0.5rem" }}
            >
              {link.name}
            </ModerationSettingsName>
            <ModerationLink
              href={link.url}
              target="_blank"
              rel="noreferrer"
              roomStyles={roomStyles}
            >
              {link.url.replace(/^https?:\/\//, "")}
            </ModerationLink>
            <Box paddingRight="1">
              <QuestionMarkIcon onClick={() => setShowOVerlay(true)} />
            </Box>
            <CopyIcon
              textToCopy={link.url}
              textForToast={t("Link copied to clipboard")}
            />
          </ModerationSettingsItem>
        ))}
      </ModerationSettingsList>
      {showOverlay && (
        <ObsInstructionModal onCancel={() => setShowOVerlay(false)} />
      )}
    </>
  )
})

const QuestionMarkIcon = ({ onClick }) => {
  const roomStyles = useRecoilValue(brandingSelector)

  const handleCopyClick = onClick

  return (
    <>
      <IconButton
        aria-label="copy-icon"
        fontSize="xs"
        size="xs"
        icon={<TbQuestionMark />}
        backgroundColor={
          roomStyles?.secondaryAccessButtonBackgroundColor ?? "#1AC541"
        }
        borderColor={
          roomStyles?.secondaryAccessButtonBorderColor ?? "transparent"
        }
        border={
          roomStyles?.secondaryAccessButtonBorderColor
            ? `1px solid ${
                roomStyles?.secondaryAccessButtonBorderColor
                  ? roomStyles.secondaryAccessButtonBorderColor
                  : "transparent"
              }`
            : "0px"
        }
        color={roomStyles?.secondaryAccessButtonTextColor ?? "#FFFFFF"}
        onClick={handleCopyClick}
        _hover={{
          backgroundColor:
            roomStyles?.secondaryAccessButtonMouseoverColor ?? "#24A840",
          color:
            roomStyles?.secondaryAccessButtonTextMouseoverColor ?? "#FFFFFF"
        }}
      />
    </>
  )
}

const ObsInstructionModal = ({ onCancel }) => {
  const roomStyles = useRecoilValue(brandingSelector)
  const { isMobile, isLandscape } = useBrowserType()

  return (
    <Box
      width="100vw"
      height="100vh"
      position="fixed"
      background="white"
      zIndex="1000000000"
      top="0"
      left="0"
      backgroundColor="rgba(255, 255, 255, 0.5)"
    >
      <Flex
        minW={isMobile && !isLandscape ? "300px" : "400px"}
        width={isMobile ? "50vw" : "500px"}
        position="absolute"
        top="50%"
        left="50%"
        padding="1rem"
        marginRight="-50%"
        transform="translate(-50%, -50%)"
        flexDirection="column"
        backgroundColor="rgba(255, 255, 255)"
        borderRadius="0.375rem"
        borderWidth="1px"
        borderStyle="solid"
        borderColor={roomStyles.servicePanelElementBorder ?? "gray.300"}
        bg={
          blendWithWhiteBackground(
            roomStyles?.servicePanelElementModalBackground
          ) ?? "white"
        }
        color={roomStyles?.servicePanelElementPrimaryText}
      >
        <Flex
          background={
            blendWithWhiteBackground(
              roomStyles?.servicePanelElementModalBackground
            ) ?? "white"
          }
          pos="absolute"
          width="19px"
          height="19px"
          borderRadius="50%"
          right="-9px"
          top="-9px"
          alignItems="center"
          justifyContent="center"
          border="1px solid"
          borderColor={roomStyles.servicePanelElementBorder ?? "#CBD5E0"}
          cursor={"pointer"}
          zIndex={1}
          onClick={onCancel}
        >
          <Icon
            src={IoClose}
            fontSize="0.8rem"
            marginLeft="1px"
            color={roomStyles?.servicePanelElementPrimaryText ?? "#000"}
          />
        </Flex>

        <Box
          fontSize="1.5rem"
          lineHeight="1.5"
          fontWeight="700"
          justifyContent="center"
          textAlign="center"
        >
          Streaming from OBS
        </Box>
        {/* {subtitle && ( */}
        <Flex
          style={{ fontSize: "13px" }}
          alignItems="center"
          justifyContent="center"
          marginTop="0.4rem"
          flexDirection="column"
        >
          <Box>
            <OrderedList>
              <ListItem>Open OBS Studio Encoder on your computer.</ListItem>
              <ListItem>
                Click <b>Settings</b> on the bottom right.
              </ListItem>
              <ListItem>
                Click the <b>Stream</b> tab on the left.
              </ListItem>
              <ListItem>
                Select <b>Custom Streaming Server</b> from the drop-down menu on
                top.
              </ListItem>
              <ListItem>
                Paste the LiveSell RTMP link in the Server field.
              </ListItem>
              <ListItem>
                Click <b>OK</b>. A feed from your camera should appear on the
                encoder.
              </ListItem>
              <ListItem>
                Click <b>Start Streaming</b>. Action! Your stream is live and
                online!
              </ListItem>
              <ListItem>
                When you're done, click <b>Stop Streaming</b> on the encoder.
              </ListItem>
            </OrderedList>

            <Text paddingTop="2.5" style={{ fontSize: "11px" }}>
              <Text fontWeight="700">OBS Output Settings:</Text>{" "}
              <b>Output Mode</b> &gt; <b>Streaming</b> - Audio Track: 1 |
              Encoder: x264 | Rescale Output: 1280x720 | Rate Control: VBR |
              Bitrate 1800 Kbps | CRF: 23 | Keyframe interval: 2 | CPU Usage
              Preset: ultrafast | Profile: baseline | Tune: zerolatency | x264
              Options: threads=6
            </Text>
          </Box>
        </Flex>
        {/* )} */}
      </Flex>
    </Box>
  )
}

export default RTMPLink
